































import { computed, defineComponent, ref, watchEffect, nextTick, Ref, toRefs } from "@vue/composition-api";
import { MessageAttachment } from "@scrile/api-provider/dist/api/MessagesProvider";
import { isImageFile, isVideoFile } from "@/lib/fileHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIcon from "@/components/BaseIcon";
import PlayButton from "@/components/PlayButton";
import MessageMediaItemStatus from "./MessageMediaItemStatus.vue";

const useVideoAttachment = (
  attachment: Ref<MessageAttachment>,
  isActive: Ref<boolean>,
  isFullScreen: Ref<boolean>,
  isProcessed: Ref<boolean>
) => {
  const isVideo = computed(() => isVideoFile(attachment.value.file));
  const videoElement = ref<HTMLVideoElement | null>(null);
  const videoStarted = ref<boolean>(false);
  const videoSrc = computed(() => {
    if (!isVideo.value) return "";
    return MediaFilesController.getVideoUrl(attachment.value.file.urlPart);
  });
  const videoThumbnail = computed(() => {
    if (!isVideo.value) return "";
    return MediaFilesController.getVideoThumbnail(attachment.value.file.urlPart, "1080p");
  });

  const playVideo = async () => {
    videoStarted.value = true;
    await nextTick();
    if (videoElement.value) await videoElement.value.play();
  };

  const stopVideo = () => {
    if (videoElement.value) videoElement.value.pause();
    videoStarted.value = false;
  };

  const shouldPlayVideo = computed(
    () => !videoStarted.value && isVideo.value && isProcessed.value && isFullScreen.value
  );
  watchEffect(() => {
    if (!isActive.value || !isFullScreen.value) stopVideo();
  });

  return {
    isVideo,
    videoSrc,
    videoThumbnail,
    videoStarted,
    shouldPlayVideo,
    playVideo,
    videoElement,
  };
};

const useImageAttachment = (attachment: Ref<MessageAttachment>) => {
  const isImage = computed(() => isImageFile(attachment.value.file));
  const imageThumbnail = computed(() => {
    if (!isImage.value) return "";
    return MediaFilesController.getImageThumbnail(attachment.value.file.urlPart, "1080p");
  });

  return {
    isImage,
    imageThumbnail,
  };
};

export default defineComponent({
  name: "MessageMediaItem",
  components: { PlayButton, MessageMediaItemStatus, BaseConfirm, BaseIcon },
  props: {
    attachment: { type: Object as () => MessageAttachment, required: true },
    isAuthor: { type: Boolean, required: true },
    isFullScreen: { type: Boolean, required: true },
    isActive: { type: Boolean, required: true },
  },
  setup(props, { emit }) {
    const { attachment, isFullScreen, isActive } = toRefs(props);
    const isProcessed = computed(() => props.attachment.file.processingStatus === "OK");

    const {
      isVideo,
      videoSrc,
      videoThumbnail,
      shouldPlayVideo,
      playVideo,
      videoStarted,
      videoElement,
    } = useVideoAttachment(attachment, isActive, isFullScreen, isProcessed);
    const { isImage, imageThumbnail } = useImageAttachment(attachment);

    const attachmentThumbnail = computed(() =>
      isImage.value ? imageThumbnail.value : isVideo.value ? videoThumbnail.value : ""
    );

    const click = () => {
      if (shouldPlayVideo.value) {
        playVideo();
      } else if (!isFullScreen.value) {
        emit("click");
      }
    };

    return {
      isProcessed,
      attachmentThumbnail,
      isImage,
      isVideo,
      videoSrc,
      videoStarted,
      click,
      videoElement,
    };
  },
});
